import type { RouteLocationNormalizedLoaded, Router } from "vue-router";
import { useTagsViewStore, type TagView } from "@shared/store/modules/tagsView";
import type { SidebarMenuItem } from "@shared/types/sidebarMenu";
import { getAppHomePath, isRegisteredAppPath } from "@shared/utils/appHome";
import { hasButtonPermission } from "@shared/utils/permission";
import { findFirstNavigableLeafPath } from "@shared/utils/sidebarMenu";

/** 关闭当前路由对应页签（对齐老站 tagsView/delView）；不负责跳转 */
export function closeCurrentTag(route: RouteLocationNormalizedLoaded) {
    const tagsStore = useTagsViewStore();
    const tagPath = (route.meta?.singletonTagPath as string | undefined) || route.path;
    tagsStore.delView({
        path: tagPath,
        fullPath: route.fullPath,
        name: String(route.name || ""),
        title: String(route.meta?.title || ""),
        query: { ...route.query },
    });
}

/** 路由可导航：已注册且不会落到 /404 */
const ACCESS_SKIP_PATHS = new Set(["/login", "/register", "/jumpLogin", "/404", "/403"]);

export type RouteAccessDenial = "forbidden" | "notfound";

function isErrorOrGuestPath(path: string): boolean {
    return ACCESS_SKIP_PATHS.has(path);
}

function resolvePermissionCode(router: Router, path: string): string {
    const resolved = router.resolve(path);
    return typeof resolved.meta?.permissionCode === "string" ? resolved.meta.permissionCode : "";
}

/** 已登录后：无路由 → 404；有路由但按钮码不过 → 403 */
export function resolveRouteAccessDenial(path: string, router: Router | null): RouteAccessDenial | null {
    if (!router || !path || isErrorOrGuestPath(path)) return null;
    const resolved = router.resolve(path);
    const name = resolved.name;
    if (name === "FallbackNotFound" || name === "NotFound" || resolved.path === "/404") {
        return "notfound";
    }
    if (!isRegisteredAppPath(path, router)) {
        return "notfound";
    }
    const code = resolvePermissionCode(router, path);
    if (code && !hasButtonPermission(code)) {
        return "forbidden";
    }
    return null;
}

/** 路由可导航：已注册、有权限，且不会落到 /404 /403 */
export function isNavigableAppPath(path: string, router: Router | null): boolean {
    if (!router || !path || isErrorOrGuestPath(path)) return false;
    return resolveRouteAccessDenial(path, router) === null;
}

function resolveTagRouteTarget(tag: TagView, router: Router): string | null {
    if (tag.affix) {
        const home = getAppHomePath(router);
        return isNavigableAppPath(home, router) ? home : null;
    }
    if (!isNavigableAppPath(tag.path, router)) return null;
    return tag.fullPath || tag.path;
}

/** 按侧栏顺序取第一个已注册的叶子 path */
export function findFirstNavigableMenuPath(menus: SidebarMenuItem[] | undefined, router: Router): string | undefined {
    if (!menus?.length) return undefined;
    for (const item of menus) {
        const path = findFirstNavigableLeafPath(item);
        if (path && isNavigableAppPath(path, router)) return path;
    }
    return undefined;
}

export interface ResolveTagsViewNavigationOptions {
    preferFromIndex?: number;
    visitedViews: TagView[];
    router: Router;
    sidebarMenus: SidebarMenuItem[];
}

/**
 * 页签关闭 / 跳转时解析目标路由：
 * 1. 优先相邻页签（左 → 右）
 * 2. 再取侧栏第一个可访问菜单
 * 3. 最后各端首页
 */
export function resolveTagsViewNavigationTarget(opts: ResolveTagsViewNavigationOptions): string {
    const { preferFromIndex, visitedViews, router, sidebarMenus } = opts;
    const candidates: TagView[] = [];
    const start = preferFromIndex ?? visitedViews.length - 1;

    for (let i = start; i >= 0; i--) {
        candidates.push(visitedViews[i]);
    }
    for (let i = start + 1; i < visitedViews.length; i++) {
        candidates.push(visitedViews[i]);
    }

    for (const tag of candidates) {
        const target = resolveTagRouteTarget(tag, router);
        if (target) return target;
    }

    const menuPath = findFirstNavigableMenuPath(sidebarMenus, router);
    if (menuPath) return menuPath;

    return getAppHomePath(router);
}
